<script setup lang="ts">
import { Loader } from '@/components/app'
import { useAuthStore } from '@/stores/auth'
import { useUnreadNotificationsQuery } from '@/composable/queries/Dara/notifications'
import { toReadableDateFormat } from '@/composable/useFormatDatesAndTimes'

const authStore = useAuthStore()

const userProfileId = computed(() => authStore.user_profile?.id ?? 0)
const isNotificationsQueryEnabled = computed(() => !!authStore.user_profile?.id)

const { data: unreadNotifications, isPending: isNotificationsPending } = useUnreadNotificationsQuery(userProfileId, isNotificationsQueryEnabled)
</script>

<template>
  <div class="d-flex align-center">
    <div class="position-relative">
      <VBtn
        icon="mdi-bell"
        variant="text"
        color="default"
        class="mr-2"
      />
      <VBadge
        v-if="authStore.unreadNotificationNumber"
        class="header-notifications-badge position-absolute"
        color="error"
        :content="authStore.unreadNotificationNumber"
        inline
      />
    </div>

    <VMenu
      activator="parent"
      width="400"
      location="bottom end"
      offset="14px"
    >
      <VList>
        <VListItem v-if="authStore.is_shadowing">
          <VListItemTitle>
            {{ $t('notifications_sidebar.not_available_for_shadowing') }}
          </VListItemTitle>
        </VListItem>
        <template v-else>
          <VListItem>
            <div class="d-flex align-center justify-space-between">
              <h4>{{ $t('notifications_sidebar.title') }}</h4>
              <VChip
                color="primary"
                variant="tonal"
              >
                {{ $t('notifications_sidebar.unread_nb', { unreadNotificationsNb: authStore.unreadNotificationNumber }) }}
              </VChip>
            </div>
          </VListItem>
          <VDivider class="my-2" />

          <VListItem v-if="!authStore.unreadNotificationNumber">
            <p class="text-center">
              {{ $t('notifications_sidebar.no_unread_notifications') }}
            </p>
            <VDivider class="my-2" />
          </VListItem>
          <template v-else>
            <Loader v-if="isNotificationsPending" />
            <template
              v-for="notification in unreadNotifications"
              v-else
              :key="notification.id"
            >
              <VListItem
                :to="{ name: 'notifications' }"
                class="notification-list-item-no-active"
              >
                <template #prepend>
                  <VListItemAction start>
                    <VAvatar
                      color="primary"
                      variant="tonal"
                      :icon="`mdi-${notification.icon}`"
                    />
                  </VListItemAction>
                </template>

                <VListItemTitle class="font-weight-semibold">
                  {{ notification.title }}
                </VListItemTitle>
                <VListItemSubtitle v-dompurify-html="notification.description" />

                <template #append>
                  <VListItemAction end>
                    <span
                      v-if="notification.created_at"
                      class="text-body-2"
                    >
                      {{ toReadableDateFormat(notification.created_at) }}
                    </span>
                  </VListItemAction>
                </template>
              </VListItem>

              <VDivider class="my-2" />
            </template>
          </template>

          <VListItem>
            <VBtn
              class="submit-btn w-100 text-uppercase"
              rounded="xl"
              variant="tonal"
              :to="{ name: 'notifications' }"
            >
              {{ $t('notifications_sidebar.read_all') }}
            </VBtn>
          </VListItem>
        </template>
      </VList>
    </VMenu>
  </div>
</template>

<style lang="scss">
.header-notifications-badge {
  top: 0;
  right: 0;
}

.notification-list-item-no-active.v-list-item--active.v-list-item--variant-text .v-list-item__overlay {
  background: transparent !important;
}
</style>
